import { Injectable } from '@angular/core';
import { FingerprintjsProAngularService } from '@fingerprintjs/fingerprintjs-pro-angular';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from './localstorage.service';

/**
 * 指紋觸發情境，直接作為 tag 的 source：app-download 是 APP 下載歸因，
 * 其餘與 /v1/member/auth/* 的端點名一致。新增觸發點時在這裡擴充。
 */
export type FpSourceAction =
  | 'app-download'
  | 'loginbyname'
  | 'loginbymobile'
  | 'loginbyemail'
  | 'socialuserlogin'
  | 'socialuserbindloginbymobile'
  | 'registerbyuserpwd'
  | 'registerbymobile'
  | 'registerbyemail'
  | 'registerbysocial';

/**
 * 收費指紋的統一入口。
 *
 * 存在的理由是「標籤只定義一次」：merchant / env 決定了後台能不能把用量拆到
 * 商戶與部署階段。目前多個商戶共用同一把 apiKey(M5+M9、M8+M10)，且 SIT/UAT
 * 與正式環境同樣計費，不帶標籤就無從歸因。
 *
 * merchant 與 tenant 兩個都帶：tenant 是後台的租戶編號，在各部署階段不一定
 * 相同(例如 M5 是 sit=28 / uat=20)，光看 tenant 認不出是哪個商戶；merchant
 * 綁在分支上固定不變，才適合當聚合鍵，所以 linkedId 用的是 merchant。
 *
 * 此分支沒有 eur 部署(environment 沒有 isEur、pipeline 也沒有 eur 這條)，
 * 因此不帶 region；日後若開歐洲站，補上 isEur 後在這裡加一行即可。
 *
 * 刻意不封裝 extendedResult 與 ignoreCache —— 這兩個是計費相關的決策，
 * 留在呼叫點才看得見成本。
 */
@Injectable({
  providedIn: 'root',
})
export class FingerprintService {
  constructor(
    private fp: FingerprintjsProAngularService,
    private localStorageService: LocalStorageService,
  ) {}

  /** 保存最近一次識別事件編號：隨 auth body 給後端，可對帳計費事件、回查單次判定 */
  private keepRequestId<T extends { requestId: string }>(res: T): T {
    if (res?.requestId) this.localStorageService.fpRequestId = res.requestId;
    return res;
  }

  /**
   * tag 是結構化明細(webhook 會原樣回傳)，linkedId 是後台可篩選的單一鍵。
   * 兩者都帶，是因為哪一個能在後台聚合用量目前尚未驗證。
   *
   * source 直接記觸發端點，追查異常用量時能定位到單一 API。
   * version 用於區分事件來自哪個 build —— service worker 會讓舊版 bundle
   * 在部署後存活一段時間，排查「事件遺失/錯位」時第一個要分的就是新舊版。
   */
  private optionsFor(action: FpSourceAction) {
    return {
      tag: {
        merchant: environment.common.merchant,
        tenant: environment.common.tenant,
        env: environment.env,
        version: environment.version,
        source: action,
      },
      linkedId: `${environment.env}-${environment.common.merchant}`,
    };
  }

  /** 標準識別：登入註冊風控用。action 傳觸發識別的端點名 */
  identify(action: FpSourceAction) {
    return this.fp.getVisitorData(this.optionsFor(action)).then(res => this.keepRequestId(res));
  }

  /** 擴展識別：需要 device / os / osVersion 時用，屬於加價項 */
  identifyExtended(action: FpSourceAction, ignoreCache = false) {
    return this.fp
      .getVisitorData({ extendedResult: true, ...this.optionsFor(action) }, ignoreCache)
      .then(res => this.keepRequestId(res));
  }
}
